import { useEffect, useState } from 'react';
import { api, type Look, catalogTransitions, transitionLabel, LEGACY_TRANSITIONS, LOUDNESS_TARGETS, QUALITIES, LAYER_FONTS,
         type Asset, type Card, type CaptionStyle, type QualityName, type TransitionKind } from '../lib/api';

// ---------------------------------------------------------------------------
// The pass over a finished cut: a music bed, its level and how it fades; how
// loud the whole thing ends up; how hard the encoder works.
//
// Called a mix and not a timeline, because that is what it is. A timeline implies
// clips you can drag against time, and this sets one bed over the whole
// programme — the sounds placed on a scene are cues, on the timeline's Cues
// lane. Naming it for what it will be rather than what it does is how a screen
// ends up promising an editor and delivering a dropdown.
//
// Everything here maps to something the render plane actually does. There is no
// control for anything the worker would ignore.
// ---------------------------------------------------------------------------

const DEFAULTS = { gainDb: -18, duck: true, fadeIn: 1, fadeOut: 2 };

export function MixPanel({ card, onChange, shelf = LEGACY_TRANSITIONS as unknown as string[], onShelve }: {
  card: Card;
  onChange: (c: Card) => void;
  /** The transitions this brand keeps to hand. */
  shelf?: string[];
  /** Put one from the vault onto the shelf. Absent when the card has no brand. */
  onShelve?: (kind: string) => Promise<void>;
}) {
  const [vault, setVault] = useState<string[] | null>(null);
  const [openingVault, setOpeningVault] = useState(false);
  const [tracks, setTracks] = useState<Asset[]>([]);
  const [images, setImages] = useState<Asset[]>([]);
  const [bgId, setBgId] = useState<string>('');
  const [tkind, setTkind] = useState<TransitionKind>('cut');
  const [tsecs, setTsecs] = useState(0.5);
  const [assetId, setAssetId] = useState<string>('');
  const [gainDb, setGainDb] = useState(DEFAULTS.gainDb);
  const [duck, setDuck] = useState(DEFAULTS.duck);
  const [fadeIn, setFadeIn] = useState(DEFAULTS.fadeIn);
  const [fadeOut, setFadeOut] = useState(DEFAULTS.fadeOut);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Only audio, and only READY audio. The API refuses anything else, and
  // offering a video file here would be offering a mistake — ffmpeg would
  // happily take its audio stream. A track still promoting has no media_key
  // yet, so the API would refuse it with a 409; better not to offer it than to
  // explain the refusal afterwards.
  useEffect(() => {
    let live = true;
    api.assets(card.brand_id)
      .then((r) => {
        if (!live) return;
        const ready = r.filter((a) => a.status === 'ready');
        setTracks(ready.filter((a) => a.kind === 'audio'));
        setImages(ready.filter((a) => a.kind === 'image' || a.kind === 'video'));   // a set may be a clip that loops (F1)
      })
      .catch((e) => { if (live) setError((e as Error).message); });
    return () => { live = false; };
  }, [card.brand_id]);

  // The card is the source of truth, not this form. Coming back to a card must
  // show what is actually set on it, and the stored mix holds a media KEY while
  // the form holds an asset id — so they are matched by key.
  useEffect(() => {
    const m = card.mix?.music;
    if (!m) { setAssetId(''); return; }
    setGainDb(m.gainDb ?? DEFAULTS.gainDb);
    setDuck(m.duck ?? DEFAULTS.duck);
    setFadeIn(m.fadeIn ?? DEFAULTS.fadeIn);
    setFadeOut(m.fadeOut ?? DEFAULTS.fadeOut);
    // Matched on media_key, because that is what the stored mix holds — the
    // promoted copy in the media bucket, not the upload the browser made.
    // Matching on storage_key here would silently show "— none —" on a card
    // that definitely has a bed.
    const found = tracks.find((t) => t.media_key === m.key);
    setAssetId(found?.id ?? '');
  }, [card.mix, tracks]);

  useEffect(() => {
    const b = card.mix?.background;
    setBgId(b ? (images.find((i) => i.media_key === b.key)?.id ?? '') : '');
  }, [card.mix, images]);

  const currentBg = card.mix?.background ?? null;

  useEffect(() => {
    const t = card.mix?.transition;
    setTkind(t?.kind ?? 'cut');
    setTsecs(t?.seconds ?? 0.5);
  }, [card.mix]);

  async function saveTransition(kind: TransitionKind, seconds: number) {
    setTkind(kind); setTsecs(seconds);
    setBusy(true); setError(null); setNote(null);
    try {
      onChange(await api.setTransition(card.id, kind === 'cut' ? null : { kind, seconds }));
      setNote(kind === 'cut'
        ? 'Turns cut straight from one to the next. Render the card to see it.'
        : 'Saved. Render the card to see it — a transition changes every join, so it renders again rather than reusing the last cut.');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  const [looks, setLooks] = useState<Look[]>([]);
  useEffect(() => { api.labLooks(card.brand_id).then((ls) => setLooks(ls.filter((l) => l.status === 'ready' && l.assets.overlay))).catch(() => setLooks([])); }, [card.brand_id]);
  async function saveLook(next: string) {
    setBusy(true); setError(null); setNote(null);
    try {
      onChange(await api.setLook(card.id, next || null));
      setNote(next ? 'Look set. Its rig is looped over the finished cut — render the card to see it.' : 'Look removed.');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function saveBackdrop(next: string) {
    setBgId(next);
    setBusy(true); setError(null); setNote(null);
    try {
      onChange(await api.setBackdrop(card.id, next ? { assetId: next } : null));
      setNote(next
        // No dim control. The render measures what THIS image needs against
        // THIS brand's ink and dims until the text on top still reads — a
        // slider here would be a control whose main use is making the result
        // worse, and the one value a creator would reach for (none) is the one
        // the renderer overrides anyway.
        ? 'Backdrop set. It is dimmed automatically so captions stay readable. Render the card to see it.'
        : 'Backdrop removed. Render the card to see the plain background.');
    } catch (e) { setError((e as Error).message); setBgId(currentBg ? bgId : ''); }
    finally { setBusy(false); }
  }

  const current = card.mix?.music ?? null;
  const dirty = current
    ? (tracks.find((t) => t.id === assetId)?.media_key !== current.key
       || (current.gainDb ?? DEFAULTS.gainDb) !== gainDb
       || (current.duck ?? DEFAULTS.duck) !== duck
       || (current.fadeIn ?? DEFAULTS.fadeIn) !== fadeIn
       || (current.fadeOut ?? DEFAULTS.fadeOut) !== fadeOut)
    : Boolean(assetId);

  async function save() {
    if (!assetId) return;
    setBusy(true); setError(null); setNote(null);
    try {
      onChange(await api.setMix(card.id, { assetId, gainDb, duck, fadeIn, fadeOut }));
      // Said plainly, because it is the part people get wrong: setting a bed
      // changes nothing about the file that already exists. The render is what
      // applies it, and it is a new render rather than a reused one precisely
      // because the mix is part of what identifies a render.
      setNote('Saved. Render the card to hear it — this changes the cut, so it renders again rather than reusing the last one.');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function saveFadeOut(seconds: number | null) {
    setBusy(true); setError(null); setNote(null);
    try {
      onChange(await api.setFadeOut(card.id, seconds));
      setNote(seconds ? `Saved. The programme fades to black over its last ${seconds}s — render the card to see it.`
        : 'Saved. The programme stops on its last frame.');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function clear() {
    setBusy(true); setError(null); setNote(null);
    try {
      onChange(await api.setMix(card.id, null));
      setAssetId('');
      setNote('Bed removed. Render the card to produce the cut without it.');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="panel-hd">
        <h3>Mix</h3>
        {current && <span className="chip">bed set</span>}
        {currentBg && <span className="chip">backdrop set</span>}
        {card.mix?.transition && <span className="chip">{card.mix.transition.kind}</span>}
        {card.mix?.loudness !== undefined && <span className="chip">{card.mix.loudness} LUFS</span>}
        {card.mix?.quality && <span className="chip">{card.mix.quality}</span>}
      </div>
      <div className="panel-bd">
        {error && <div className="err">{error}</div>}
        {note && (
          <div className="err" style={{ borderColor: 'var(--green)', color: 'var(--green)' }}>
            {note}
          </div>
        )}

        <label className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <span>Speakers as</span>
          <select value={card.mix?.panels === 'box' ? 'box' : 'card'} disabled={busy}
                  title="Cards: the drawn panels. Boxes: full-bleed faces in thin white-bordered rectangles with the name on a band — the double box, the four box, the five box with the host first."
                  onChange={(e) => {
                    setBusy(true); setError(null);
                    api.setPanels(card.id, e.target.value as 'card' | 'box')
                       .then((c) => { onChange(c); setNote(e.target.value === 'box' ? 'Boxes. Faces fill their frames; names sit on a band.' : 'Cards again.'); })
                       .catch((err) => setError(err instanceof Error ? err.message : 'could not save that'))
                       .finally(() => setBusy(false));
                  }}>
            <option value="card">cards — drawn panels</option>
            <option value="box">boxes — faces full-bleed, thin border, name band</option>
          </select>
        </label>
        {card.mix?.panels === 'box' && (
          <label className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <span>Box line</span>
            <select value={card.mix?.box?.line ?? 'light'} disabled={busy}
                    title="The quiet boxes' border. The speaking box keeps the accent."
                    onChange={(e) => {
                      setBusy(true); setError(null);
                      api.setBox(card.id, { ...card.mix?.box, line: e.target.value })
                         .then((c) => { onChange(c); setNote('Box line set.'); })
                         .catch((err) => setError(err instanceof Error ? err.message : 'could not save that'))
                         .finally(() => setBusy(false));
                    }}>
              <option value="light">light — near white</option>
              <option value="accent">the accent on every box</option>
              <option value="#ffffff">white</option>
              <option value="#000000">black</option>
            </select>
            <span>width</span>
            <input type="number" min={2} max={10} step={1} style={{ width: 56 }} disabled={busy}
                   value={card.mix?.box?.width ?? 5}
                   onChange={(e) => {
                     const width = Math.max(2, Math.min(10, Number(e.target.value) || 5));
                     setBusy(true); setError(null);
                     api.setBox(card.id, { ...card.mix?.box, width })
                        .then((c) => onChange(c))
                        .catch((err) => setError(err instanceof Error ? err.message : 'could not save that'))
                        .finally(() => setBusy(false));
                   }} />
          </label>
        )}
        <label className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <input type="checkbox" checked={card.mix?.captions !== false} disabled={busy}
                 onChange={(e) => {
                   setBusy(true); setError(null);
                   api.setCaptions(card.id, e.target.checked)
                     .then((c) => { onChange(c); setNote(e.target.checked
                       ? 'Each line shows on screen again.'
                       : 'Lines stay off the screen. The captions file still carries them.'); })
                     .catch((err) => setError(err instanceof Error ? err.message : 'could not save that'))
                     .finally(() => setBusy(false));
                 }} />
          <span>Show each line on screen</span>
        </label>
        <div className="sub-dim" style={{ marginBottom: 12 }}>
          The spoken line, burned in under the panels or the footage. Off keeps the picture
          clean; the captions file is written either way.
        </div>

        {/* How the burned-in line looks (086). Every control here is a thing
            the renderer draws differently; the captions file is unchanged. */}
        {card.mix?.captions !== false && (() => {
          const cs: CaptionStyle = card.mix?.captionStyle ?? {};
          const set = (patch: CaptionStyle) => {
            setBusy(true); setError(null);
            api.setCaptionStyle(card.id, { ...cs, ...patch })
              .then((c) => { onChange(c); setNote('Caption style saved. Render the card to see it; the preview frame shows it now.'); })
              .catch((err) => setError(err instanceof Error ? err.message : 'could not save that'))
              .finally(() => setBusy(false));
          };
          return (
            <div style={{ marginBottom: 18 }} data-tool="captions">
              <label className="lbl">Caption style</label>
              <div className="row wrap" style={{ gap: 8 }}>
                <select value={cs.position ?? 'bottom'} disabled={busy} title="Where the line sits"
                        onChange={(e) => set({ position: e.target.value as 'bottom' | 'top' })}>
                  <option value="bottom">bottom</option>
                  <option value="top">top</option>
                </select>
                <select value={cs.size ?? 'medium'} disabled={busy} title="How big the line is"
                        onChange={(e) => set({ size: e.target.value as 'small' | 'medium' | 'large' })}>
                  <option value="small">small</option>
                  <option value="medium">medium</option>
                  <option value="large">large</option>
                </select>
                <select value={cs.font ?? 'sans'} disabled={busy} title="Which face the line is set in"
                        onChange={(e) => set({ font: e.target.value as CaptionStyle['font'] })}>
                  {LAYER_FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
                <label className="row" style={{ gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={cs.plate !== false} disabled={busy}
                         onChange={(e) => set({ plate: e.target.checked })} />
                  <span>on a plate</span>
                </label>
              </div>
              <div className="sub-dim">
                On a plate the line reads over any footage; off the plate it is outlined
                instead. A lower third on screen lifts the line above the band.
              </div>
            </div>
          );
        })()}

        {/* How loud the finished cut is. The mix pass measures the cut and
            brings it to this; the platforms then have nothing to correct. */}
        <label className="lbl">Loudness</label>
        <div className="row wrap" style={{ marginBottom: 4 }}>
          <select value={card.mix?.loudness ?? -16} disabled={busy}
                  title="The finished cut's integrated loudness, measured and set in the mix pass"
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setBusy(true); setError(null);
                    api.setLoudness(card.id, v === -16 ? null : v)
                      .then((c) => { onChange(c); setNote(`The cut will be brought to ${v} LUFS. Render the card to hear it.`); })
                      .catch((err) => setError(err instanceof Error ? err.message : 'could not save that'))
                      .finally(() => setBusy(false));
                  }}>
            {LOUDNESS_TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="sub-dim" style={{ marginBottom: 18 }}>
          Measured over the whole programme after the bed and the cues are in, with a
          true-peak ceiling of −1 dB. Every clip is already brought to −16 on ingest.
        </div>

        {/* How hard the encoder works. One setting used to be hard-coded. */}
        <label className="lbl">Quality</label>
        <div className="row wrap" style={{ marginBottom: 4 }} data-tool="quality">
          <select value={card.mix?.quality ?? 'standard'} disabled={busy}
                  title={QUALITIES.find((q) => q.id === (card.mix?.quality ?? 'standard'))?.hint}
                  onChange={(e) => {
                    const q = e.target.value as QualityName;
                    setBusy(true); setError(null);
                    api.setQuality(card.id, q === 'standard' ? null : q)
                      .then((c) => { onChange(c); setNote(`${q} quality. Render the card to make it — ${QUALITIES.find((x) => x.id === q)?.hint}.`); })
                      .catch((err) => setError(err instanceof Error ? err.message : 'could not save that'))
                      .finally(() => setBusy(false));
                  }}>
            {QUALITIES.map((q) => <option key={q.id} value={q.id}>{q.label}</option>)}
          </select>
        </div>
        <div className="sub-dim" style={{ marginBottom: 18 }}>
          Draft renders quickly for a check. High takes about twice as long and is for a
          master worth keeping. The sound's bitrate follows: 128, 192 or 256 kbps.
        </div>

        <label className="lbl">Between turns</label>
        <div className="row wrap" style={{ marginBottom: 4 }}>
          <select value={tkind} disabled={busy}
                  onChange={(e) => void saveTransition(e.target.value as TransitionKind, tsecs)}>
            {/* The shelf. Fifty-nine in a dropdown is a list nobody reads;
                four is a choice. The rest are behind "More…", which is the
                vault — and picking one from there puts it on the shelf, so
                the next card starts from what this creator actually uses. */}
            {shelf.map((k) => (
              <option key={k} value={k}>{transitionLabel(k)}</option>
            ))}
            {!shelf.includes(tkind) && tkind && (
              // A card already set to something off the shelf must still show
              // what it is set to. A select whose value is not among its
              // options renders blank, which reads as "no transition".
              <option value={tkind}>{transitionLabel(tkind)}</option>
            )}
          </select>
          {/* The vault. Fifty-five more, fetched only when asked for — the
              whole point of the shelf is that nobody scrolls this by default.
              Choosing one puts it on the shelf, so the next card starts from
              what this creator actually reaches for. */}
          {onShelve && (vault === null ? (
            <button className="btn tiny" disabled={busy || openingVault}
                    onClick={() => {
                      setOpeningVault(true);
                      catalogTransitions()
                        .then((r) => setVault(r.transitions.filter((k) => !shelf.includes(k))))
                        .catch(() => setNote('the transition vault is unavailable just now'))
                        .finally(() => setOpeningVault(false));
                    }}>
              {openingVault ? 'Opening…' : 'More…'}
            </button>
          ) : (
            <select value="" disabled={busy}
                    onChange={(e) => {
                      const k = e.target.value;
                      if (!k) return;
                      // Shelve it AND set it. Picking from the vault is the
                      // creator saying they want this one now, not filing it
                      // for later.
                      void onShelve(k).then(() => saveTransition(k, tsecs));
                    }}>
              <option value="">Vault — {vault.length} more…</option>
              {vault.map((k) => <option key={k} value={k}>{transitionLabel(k)}</option>)}
            </select>
          ))}
          {tkind !== 'cut' && (
            <input
              type="number" min={0.1} max={3} step={0.1} value={tsecs} disabled={busy}
              onChange={(e) => void saveTransition(tkind, Number(e.target.value))}
              style={{ width: 90 }} title="seconds"
            />
          )}
        </div>
        <div className="sub-dim" style={{ marginBottom: 18 }}>
          A cut is instant and costs nothing extra. A transition is trimmed to
          fit the shorter turn it joins, and the programme stays exactly as long
          either way — the narration does not move.
        </div>

        <label className="lbl">At the end</label>
        <div className="row wrap" style={{ marginBottom: 4 }} data-tool="mix-fade-out">
          <select value={card.mix?.fadeOut ?? ''} disabled={busy}
                  onChange={(e) => void saveFadeOut(e.target.value ? Number(e.target.value) : null)}>
            <option value="">stops on the last frame</option>
            <option value="1">fades to black · 1s</option>
            <option value="2">fades to black · 2s</option>
            <option value="3">fades to black · 3s</option>
            <option value="5">fades to black · 5s</option>
          </select>
        </div>
        <div className="sub-dim" style={{ marginBottom: 18 }}>
          Picture and sound go together, over the last seconds of the last scene — the
          programme stays exactly as long. The same control sits at the end of the
          Joins lane on the timeline.
        </div>

        <label className="lbl">A look from the Lab</label>
        <div style={{ marginBottom: 18 }} data-tool="mix-look">
          <div className="row wrap" style={{ alignItems: 'center', gap: 8 }}>
            <select value={card.mix?.look?.id ?? ''} disabled={busy} onChange={(e) => void saveLook(e.target.value)}>
              <option value="">— no overlay —</option>
              {looks.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.kind}</option>)}
            </select>
            <span className="sub-dim">{looks.length === 0 ? 'Make a rig in the Lab and it appears here.' : 'The rig is looped over the finished cut, after the banners. A made plate goes in as the backdrop below.'}</span>
          </div>
        </div>

        <label className="lbl">Backdrop</label>
        {images.length === 0 ? (
          <div className="empty" style={{ marginBottom: 18 }}>
            No images in this brand’s library yet. Upload one in Assets and it
            appears here.
          </div>
        ) : (
          <div style={{ marginBottom: 18 }}>
            <div className="row wrap">
              <select value={bgId} disabled={busy}
                      onChange={(e) => void saveBackdrop(e.target.value)}>
                <option value="">— plain background —</option>
                {images.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name?.trim() || (i.media_key ?? i.storage_key).split('/').pop()}
                  </option>
                ))}
              </select>
            </div>
            <div className="sub-dim">
              Sits behind the whole programme. It is dimmed as far as the text on
              top of it needs and no further, so a bright photo stays readable and
              a dark one keeps its depth.
            </div>
          </div>
        )}

        <label className="lbl">Music</label>
        {tracks.length === 0 ? (
          // An empty state that says where the thing comes from. A bare "no
          // music" leaves somebody looking for an upload button that is on a
          // different screen.
          <div className="empty">
            No audio in this brand’s library yet. Upload a track in Assets and it
            appears here.
          </div>
        ) : (
          <>
            <div className="row wrap">
              <select value={assetId} onChange={(e) => setAssetId(e.target.value)} disabled={busy}>
                <option value="">— none —</option>
                {tracks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name?.trim() || (t.media_key ?? t.storage_key).split('/').pop()}
                    {t.duration_ms ? ` · ${Math.round(t.duration_ms / 1000)}s` : ''}
                  </option>
                ))}
              </select>
            </div>

            {assetId && (
              <>
                <label className="lbl" style={{ marginTop: 14 }}>
                  Level · {gainDb} dB under the speech
                </label>
                {/* Capped at 0. Above it the music is louder than the voice,
                    which the API and the database both refuse — so the control
                    cannot ask for something the save would reject. */}
                <input
                  type="range" min={-40} max={0} step={1} value={gainDb} disabled={busy}
                  onChange={(e) => setGainDb(Number(e.target.value))}
                  style={{ width: '100%', maxWidth: 420 }}
                />
                <div className="sub-dim">
                  −18 dB is the default: present, and never competing with a word.
                </div>

                <label className="row" style={{ marginTop: 14, gap: 8, alignItems: 'center' }}>
                  <input
                    type="checkbox" checked={duck} disabled={busy}
                    onChange={(e) => setDuck(e.target.checked)}
                  />
                  <span>Duck under speech</span>
                </label>
                <div className="sub-dim">
                  Drops the bed about 11 dB whenever somebody is talking and lets it
                  back up in the gaps. Turn it off for a cold open or a montage.
                </div>

                <div className="row wrap" style={{ marginTop: 14, gap: 16 }}>
                  <div>
                    <label className="lbl">Fade in (s)</label>
                    <input
                      type="number" min={0} max={30} step={0.5} value={fadeIn} disabled={busy}
                      onChange={(e) => setFadeIn(Number(e.target.value))}
                      style={{ width: 90 }}
                    />
                  </div>
                  <div>
                    <label className="lbl">Fade out (s)</label>
                    <input
                      type="number" min={0} max={30} step={0.5} value={fadeOut} disabled={busy}
                      onChange={(e) => setFadeOut(Number(e.target.value))}
                      style={{ width: 90 }}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="row wrap" style={{ marginTop: 16, gap: 8 }}>
              <button className="btn pri" disabled={busy || !assetId || !dirty} onClick={() => void save()}>
                {busy ? 'Saving…' : current ? 'Update the mix' : 'Set the bed'}
              </button>
              {current && (
                <button className="btn" disabled={busy} onClick={() => void clear()}>
                  Remove
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
